import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy} from '@angular/core';
import {DeviceMeasurement} from '../../../../model/device-measurement';
import {AssetMeasurement} from '../../../../model/asset-measurement';
import {Kpi} from '../../../../model/kpi';
import {
  SynopticConfiguration,
  SynopticConfigurationComponent,
  SynopticElementEvent,
  SynopticElementState,
  SynopticElementStatic
} from '../../../../model/synoptics/synoptic-configuration-types';
import {Box, Element, List, Matrix, SVG, Text} from '@svgdotjs/svg.js';
import {SynopticsService} from '../../../../services/synoptics.service';
import {Router} from '@angular/router';
import {SensorIdentifier} from '../../../../model/sensor-identifier';
import {DevicesService} from '../../../../services/devices.service';
import {AssetsService} from '../../../../services/assets.service';
import {KpisService} from '../../../../services/kpis.service';
import {
  ConversionFactorOperation,
  ExecuteCommandArgs,
  OriginAnchor,
  ReplaceTextValueArgs,
  ShowPopupArgs,
  ShowTooltipArgs,
  Style,
  StyleAttribute,
  StyleByContinuousValueArgs,
  StyleByThresholdsArgs,
  SynopticItemUuids
} from '../../../../model/synoptics/synoptic-arg-types';
import {translate} from '@ngneat/transloco';
import {ConfirmationService, MessageService} from 'primeng/api';
import {fade} from '../../../../common/animations';
import {SynopticFile} from '../../../../model/synoptics/synoptic-file';
import {CommandsSchema, CommandsSchemas} from '@iotsens/commands';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {
  SynopticVisualizationCommandParamsComponent
} from './synoptic-visualization-command-params/synoptic-visualization-command-params.component';
import {cloneDeep} from 'lodash';
import {take} from 'rxjs/operators';
import TinyColors from 'tinycolor2';

type Layer = { component: SynopticConfigurationComponent, element: Element };

@Component({
  selector: 'app-synoptic-visualization',
  templateUrl: './synoptic-visualization.component.html',
  styleUrls: ['./synoptic-visualization.component.scss'],
  animations: [fade]
})
export class SynopticVisualizationComponent implements AfterViewInit, OnDestroy {

  @Input() synopticConfiguration: string;
  @Input() synopticFiles: SynopticFile[];
  @Input() offsetX: number = 0;
  @Input() offsetY: number = 0;

  public configuration: SynopticConfiguration;

  public deviceMeasurements: DeviceMeasurement[];
  public assetMeasurements: AssetMeasurement[];
  public kpis: Kpi[];

  public layers: Layer[];
  public autoRefreshInterval: any;
  public commandsSchemaMap: Map<string, CommandsSchemas>;
  public baseReady: boolean;

  public tooltipArgs: ShowTooltipArgs;
  public tooltipVisible: boolean;
  public tooltipX: number;
  public tooltipY: number;

  constructor(
    private synopticsService: SynopticsService,
    private devicesService: DevicesService,
    private assetsService: AssetsService,
    private kpisService: KpisService,
    private messageService: MessageService,
    public readonly elementRef: ElementRef,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.deviceMeasurements = [];
    this.assetMeasurements = [];
    this.kpis = [];
    this.layers = [];
    this.tooltipVisible = false;
    this.commandsSchemaMap = new Map<string, CommandsSchemas>();
    this.baseReady = false;
  }

  ngAfterViewInit(): void {
    this.loadSynoptic();
  }

  private loadSynoptic(): void {
    this.configuration = JSON.parse(this.synopticConfiguration);
    this.loadMeasurements().then(() => {
      this.loadBaseSvgFile(this.configuration.file);
      this.autoRefreshInterval = setInterval(() => this.refreshSynoptic(), 1000 * 10);
    });
  }

  private loadBaseSvgFile(fileName: string): void {
    const file: SynopticFile = this.synopticFiles.find(f => f.name === fileName);
    const layer: Layer = {component: this.configuration, element: null};
    this.layers.push(layer);

    this.cdr.detectChanges();

    layer.element = SVG()
      .size('100%', '100%')
      .css('zIndex', '-1')
      .svg(this.synopticsService.decodeSVG(file.data).dataDecoded)
        .addTo('#container-layer-0')
        .get(0)
        .attr({ width: null, height: null });

      this.applyStatics(layer);
      this.applyStates(layer);
      this.applyEvents(layer);

      this.baseReady = true;

  }

  private refreshSynoptic(): void {
    this.loadMeasurements().then((): void => {
      for (const layer of this.layers) {
        this.applyStates(layer);
      }
    });
  }

  private loadPopupSvgFile(componentName: string): void {
    const component: SynopticConfigurationComponent = this.configuration.children.find(c => c.name === componentName);
    const file: SynopticFile = this.synopticFiles.find(f => f.name === component.file);
    const layer: Layer = {component: component, element: null};
    this.layers.push(layer);

    this.cdr.detectChanges();

    layer.element = SVG()
      .svg(this.synopticsService.decodeSVG(file.data).dataDecoded)
      .css('top', '50%')
      .css('left', '50%')
      .css('position', 'absolute')
      .css('transform', 'translate(-50%, -50%)')
      .addTo('#container-layer-' + (this.layers.length - 1));

    const child = layer.element.get(0);
    layer.element.size(child.width(), child.height());

    this.applyStatics(layer);
    this.applyStates(layer);
    this.applyEvents(layer);
  }

  private applyStates(layer: Layer): void {
    layer.component.states.forEach((state: SynopticElementState): void => {
      let element: Element = this.findChildElementById(layer.element, state.id);
      if (element) {
        if (state.modifier.type === 'styleByThresholds') {
          this.applyStyleByThresholds(element, state.modifier.args as StyleByThresholdsArgs, state.sensorIdentifier);
        } else if (state.modifier.type === 'styleByContinuousValue') {
          this.applyStyleByContinuousValue(element, state.modifier.args as StyleByContinuousValueArgs, state.sensorIdentifier);
        } else if (state.modifier.type === 'replaceTextValue') {
          this.applyReplaceTextValue(element, state.modifier.args as ReplaceTextValueArgs, state.sensorIdentifier);
        }
      }
    });
  }

  private applyStyleByThresholds(element: Element, args: StyleByThresholdsArgs, sensorIdentifier: SensorIdentifier): void {
    let lastValue: number = +this.getMeasurementOfSensorIdentifier(sensorIdentifier)?.lastValueReported;

    if (args.defaultStyle) {
      this.applyStyleToElement(element, args.defaultStyle, args.attributes, args.originAnchor);
    }

    for (const threshold of args.thresholds) {
      if (lastValue >= threshold.from && (lastValue === threshold.from || lastValue < threshold.to)) {
        this.applyStyleToElement(element, threshold, args.attributes, args.originAnchor);
      }
    }
  }

  private applyStyleToElement(element: Element, style: Style, attributes: StyleAttribute[], originAnchor: OriginAnchor): void {
    if (attributes.includes('visibility')) {
      element.css('opacity', `${style.opacityPercentage}%`);
      element.css('visibility', style.visible ? 'visible' : 'hidden');
    }
    if (attributes.includes('backgroundColor')) {
      element.css('fill', style.backgroundColor);
    }

    const resizeX: boolean = attributes.includes('widthPercentage');
    const resizeY: boolean = attributes.includes('heightPercentage');
    const widthPercentage: number = style.widthPercentage / 100;
    const heightPercentage: number = style.heightPercentage / 100;
    this.applyTransform(element, originAnchor, resizeX, resizeY, widthPercentage, heightPercentage);
  }

  private applyStyleByContinuousValue(element: Element, args: StyleByContinuousValueArgs, sensorIdentifier: SensorIdentifier): void {
    let lastValue: number = +this.getMeasurementOfSensorIdentifier(sensorIdentifier)?.lastValueReported;

    const valuePercentage: number = this.toPercent(lastValue, args.valueRange[0], args.valueRange[1]);

    if (args.attributes.includes('backgroundColor')) {
      const backgroundColorLerp = TinyColors.mix(
        args.backgroundColorRange[0],
        args.backgroundColorRange[1],
        valuePercentage * 100
      ).toHexString();
      element.css('fill', backgroundColorLerp);
    }

    const resizeX: boolean = args.attributes.includes('widthPercentage');
    const resizeY: boolean = args.attributes.includes('heightPercentage');
    const widthRange: number[] = args.widthPercentageRange ?? [0, 100];
    const heightRange: number[] = args.heightPercentageRange ?? [0, 100];
    const widthPercentage: number = (widthRange[0] + (widthRange[1] - widthRange[0]) * valuePercentage) / 100;
    const heightPercentage: number = (heightRange[0] + (heightRange[1] - heightRange[0]) * valuePercentage) / 100;
    this.applyTransform(element, args.originAnchor, resizeX, resizeY, widthPercentage, heightPercentage);
  }

  private applyReplaceTextValue(element: Element, args: ReplaceTextValueArgs, sensorIdentifier: SensorIdentifier): void {
    let measurement: AssetMeasurement | Kpi | DeviceMeasurement = this.getMeasurementOfSensorIdentifier(sensorIdentifier);
    if (measurement) {
      let convertedValue: number = this.applyConversionFactor(
        args.conversionFactorOperation, +measurement.lastValueReported, args.conversionFactor);
      let textValue: string = (convertedValue).toFixed(args.decimalPlaces ?? 2);
      if (args.showUnits) {
        const measurementWithUnit: DeviceMeasurement | AssetMeasurement = measurement as DeviceMeasurement | AssetMeasurement;
        if (measurementWithUnit.measurementType?.unit) {
          textValue += ' ' + measurementWithUnit.measurementType.unit;
        }
      }
      (element as Text)?.text(textValue);
    }
  }

  private applyConversionFactor(operation: ConversionFactorOperation, value: number, conversionFactor: number): number {
    switch (operation) {
      case 'addition':
        return value + conversionFactor;
      case 'subtraction':
        return value - conversionFactor;
      case 'multiplication':
        return value * conversionFactor;
      case 'division':
        return value / conversionFactor;
      default:
        return value;
    }
  }

  private applyEvents(layer: Layer): void {
    layer.component.events.forEach((event: SynopticElementEvent): void => {
      let element: Element = this.findChildElementById(layer.element, event.id);
      if (element) {
        if (event.trigger === 'click') {
          if (event.action.type === 'executeCommand') {
            element.css('cursor', 'pointer');
            element.click(() => this.askForCommandExecutionConfirmation(event.action.args));
          } else if (event.action.type === 'showPopup') {
            element.css('cursor', 'pointer');
            element.click(() => this.showPopup(event.action.args));
          } else if (event.action.type === 'closePopup') {
            element.css('cursor', 'pointer');
            element.click(() => this.layers.pop());
          } else if (event.action.type === 'linkToSynoptic') {
            element.css('cursor', 'pointer');
            element.click(() => this.router.navigate(['synoptics', event.action.args.synopticUuid, 'details']));
          }
        } else if (event.trigger === 'hover') {
          if (event.action.type === 'showTooltip') {
            element.mouseover((): void => {
              this.tooltipArgs = (event.action.args as ShowTooltipArgs);
              this.tooltipX = element.node.getBoundingClientRect().left + (element.node.getBoundingClientRect().width / 2);
              this.tooltipY = element.node.getBoundingClientRect().top + (element.node.getBoundingClientRect().height / 2);
              this.tooltipVisible = true;
            });
            element.mouseout((): void => {
              this.tooltipVisible = false;
            });
          }
        }
      }
    });
  }

  private askForCommandExecutionConfirmation(args: ExecuteCommandArgs): void {
    if (!args.askParamsToUser) {
      this.confirmationService.confirm({
        icon: 'pi pi-exclamation-triangle',
        message: translate('info.confirmCommandExecution', {command: args.command}),
        header: translate('forms.confirmation'),
        acceptLabel: translate('common.accept'),
        rejectLabel: translate('common.cancel'),
        acceptIcon: 'pi pi-check',
        defaultFocus: 'none',
        acceptButtonStyleClass: 'p-button-danger p-button-text',
        accept: () => this.executeCommand(args)
      });

    } else {
      this.loadDeviceSchemas(args.deviceUuid).then((schemas: CommandsSchema[]): void => {
        let params;

        try {
          const commands = schemas.find(s => s.name === args.schema).commands;
          for (const key of Object.keys(commands)) {
            if (key === args.command) {
              params = commands[key]['params'];
            }
          }
        } catch (e) {
          const summary = translate('warning.summary.invalidConfiguration');
          const detail = translate('warning.synoptics.noCommandFound', {
            schema: args.schema, command: args.command
          });
          this.messageService.add({severity: 'warn', summary, detail, sticky: true});
          return;
        }

        const ref: DynamicDialogRef = this.dialogService.open(SynopticVisualizationCommandParamsComponent, {
          header: translate('misc.executeCommand', {command: args.command}),
          style: {width: 'min(700px, 100vw)', maxHeight: '75vh'},
          data: {paramsFromSchema: params, args}
        });

        ref.onClose.pipe(take(1)).subscribe((userParams: any): void => {
          if (userParams) {
            const argsWithUserParams: ExecuteCommandArgs = cloneDeep(args);
            argsWithUserParams.params = userParams;
            this.executeCommand(argsWithUserParams);
          }
        });
      });
    }
  }

  private loadDeviceSchemas(deviceUuid: string): Promise<CommandsSchema[]> {
    if (this.commandsSchemaMap.has(deviceUuid)) {
      return Promise.resolve(this.commandsSchemaMap.get(deviceUuid).commandSchemas);
    } else {
      return this.devicesService.getDeviceCommandSchemasByUuid(deviceUuid).toPromise().then((response: CommandsSchemas) => {
        this.commandsSchemaMap.set(deviceUuid, response);
        return response.commandSchemas;
      });
    }
  }

  private executeCommand(args: ExecuteCommandArgs): void {
    const exec = {commandName: args.command, parameters: args.params};
    this.devicesService.executeDeviceCommand(args.deviceUuid, args.schema, args.command, exec).toPromise().then((): void => {
      const summary = translate('success.generic.changesApplied');
      const detail = translate('success.command.executed');
      this.messageService.add({severity: 'success', summary, detail, sticky: false});
    }, (): void => {
      const summary = translate('error.summary.unexpected');
      const detail = translate('error.command.executed');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
    });
  }

  private applyStatics(layer: Layer): void {
    layer.component.statics.forEach((staticReplacement: SynopticElementStatic): void => {
      let element: Element = this.findChildElementById(layer.element, staticReplacement.id);
      if (element) {
        if (staticReplacement.attribute === 'text') {
          (element as Text)?.text(staticReplacement.value);
        }
      }
    });
  }

  private showPopup(args: ShowPopupArgs): void {
    this.loadPopupSvgFile(args.component);
  }

  public getMeasurementOfSensorIdentifier(sensorIdentifier: SensorIdentifier): AssetMeasurement | Kpi | DeviceMeasurement {
    switch (sensorIdentifier?.entityType) {
      case 'deviceMeasurement':
        return this.deviceMeasurements.find(dm => dm.uuid === sensorIdentifier.sensorUuid);
      case 'assetMeasurement':
        return this.assetMeasurements.find(am => am.uuid === sensorIdentifier.sensorUuid);
      case 'kpi':
        return this.kpis.find(k => k.uuid === sensorIdentifier.entityUuid);
    }
  }

  private findChildElementById(parentElement: Element, childId: string): Element {
    const found: List<Element> = parentElement.find('#' + childId);
    if (found.length === 0) {
      const summary = translate('warning.summary.invalidConfiguration');
      const detail = translate('warning.synoptics.noElementsWithId', {id: childId});
      this.messageService.add({severity: 'warn', summary, detail, sticky: true});
      return null;
    }
    if (found.length > 1) {
      const summary = translate('warning.summary.invalidConfiguration');
      const detail = translate('warning.synoptics.multipleElementsSameId', {id: childId});
      this.messageService.add({severity: 'warn', summary, detail, sticky: true});
    }
    return found[0];
  }

  private loadMeasurements(): Promise<any> {
    let uuids: SynopticItemUuids = this.synopticsService.findAllMeasurementUuidsInConfig(this.configuration);
    return Promise.all([
      uuids.deviceMeasurementUuids.length > 0 ? this.loadDeviceMeasurements(uuids.deviceMeasurementUuids) : Promise.resolve(),
      uuids.assetMeasurementUuids.length > 0 ? this.loadAssetMeasurements(uuids.assetMeasurementUuids) : Promise.resolve(),
      uuids.kpiUuids.length > 0 ? this.loadKpiMeasurements(uuids.kpiUuids) : Promise.resolve()
    ]);
  }

  private loadDeviceMeasurements(uuids: string[]): Promise<any> {
    return this.devicesService.getDeviceMeasurements(uuids).toPromise().then(response => {
      this.deviceMeasurements = response.deviceMeasurements;
    });
  }

  private loadAssetMeasurements(uuids: string[]): Promise<any> {
    return this.assetsService.getAssetMeasurements(uuids).toPromise().then(response => {
      this.assetMeasurements = response.assetMeasurements;
    });
  }

  private loadKpiMeasurements(uuids: string[]): Promise<any> {
    return this.kpisService.getKpisFromUuids(uuids).toPromise().then(response => {
      this.kpis = response.kpis;
    });
  }

  private applyTransform(
    element: Element,
    originAnchor: OriginAnchor,
    resizeX: boolean,
    resizeY: boolean,
    widthPercentage: number,
    heightPercentage: number
  ): void {
    if (!resizeX && !resizeY) {
      return;
    }
    const anchorX: string = originAnchor.split(' ')[1];
    const anchorY: string = originAnchor.split(' ')[0];
    const originXAttr: 'x' | 'cx' | 'x2' = anchorX === 'left' ? 'x' : anchorX === 'center' ? 'cx' : 'x2';
    const originYAttr: 'y' | 'cy' | 'y2' = anchorY === 'top' ? 'y' : anchorY === 'center' ? 'cy' : 'y2';
    const bBox: Box = element.bbox();
    const scaleX: number = resizeX ? widthPercentage : 1;
    const scaleY: number = resizeY ? heightPercentage : 1;
    const originX: number = bBox[originXAttr];
    const originY: number = bBox[originYAttr];
    const transformMatrix: Matrix = new Matrix().scale(scaleX, scaleY, originX, originY);
    element.transform(transformMatrix);
  }

  private toPercent(value: number, min: number, max: number): number {
    const m: number = 1 / (max - min);
    const b: number = 1 / (1 - (max / min));
    const res: number = (m * value) + b;
    if (res <= 0)
      return 0;
    if (res >= 1)
      return 1;
    return res;
  }

  ngOnDestroy(): void {
    clearInterval(this.autoRefreshInterval);
  }

}
